<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper/types';

const props = withDefaults(defineProps<{
  items?: unknown[];
  loop?: boolean;
  keyboard?: boolean;
  pagination?: boolean;
  slides?: number | 'auto';
  slideToClickedSlide?: boolean;
}>(), {
  items: () => [],
  loop: true,
  keyboard: false,
  pagination: true,
  slides: 1,
  slideToClickedSlide: true,
});

const modules = [Keyboard, Pagination, Navigation];

defineSlots<{
  default?(props: { item: unknown }): void;
}>();

const slideNumber = defineModel<number>('index', { default: 0 });

function updateSlideNumber(swiper: SwiperClass) {
  slideNumber.value = swiper.realIndex;
}

const { items } = toRefs(props);
const swiper = ref<SwiperClass>();

const initialSlide = ref(slideNumber.value);
watch(slideNumber, () => {
  if (slideNumber.value !== swiper.value?.realIndex) {
    swiper.value?.slideToLoop(slideNumber.value);
  }
});
</script>

<template lang="pug">
Swiper(
  :slidesPerView="slides"
  :loop="items.length > 2 && loop"
  :threshold="5"
  :spaceBetween="10"
  :navigation="{prevEl: '.prev-slide', nextEl: '.next-slide'}"
  :pagination="pagination ? {clickable: true} : false"
  :modules="modules"
  :initialSlide="initialSlide"
  :centeredSlides="true"
  :slideToClickedSlide="slideToClickedSlide"
  :loopAdditionalSlides="10"
  :maxBackfaceHiddenSlides="20"
  @realIndexChange="updateSlideNumber"
  @swiper="swiper = $event"
)
  SwiperSlide.swiper-slide(
    v-for="item of items"
    v-slot="{ isActive }"
    :key="item.id"
  )
    slot(:item="item" :isActive="isActive")
</template>

<style scoped lang="scss">
.swiper-slide {
  --uno: flex;
  width: unset;
}
</style>